import React, { useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Cookie, 
  Shield, 
  Settings, 
  BarChart3, 
  Target, 
  Eye,
  Check,
  X,
  Info,
  Mail
} from 'lucide-react';

// Types
type CookieType = 'necessary' | 'analytics' | 'functional' | 'marketing';
type CookiePreferences = Record<CookieType, boolean>;

// Constants outside component to prevent recreation
const LAST_UPDATED = "December 1, 2023";

const COOKIE_TYPES = [
  {
    type: 'necessary' as const,
    title: 'Necessary Cookies',
    icon: <Shield className="w-5 h-5" />,
    description: 'Essential for the website to function properly. These cookies cannot be disabled.',
    alwaysActive: true,
    examples: ['Session management', 'Security features', 'Load balancing']
  },
  {
    type: 'analytics' as const,
    title: 'Analytics Cookies',
    icon: <BarChart3 className="w-5 h-5" />,
    description: 'Help us understand how visitors interact with our website.',
    alwaysActive: false,
    examples: ['Page visits', 'Time on site', 'Error tracking']
  },
  {
    type: 'functional' as const,
    title: 'Functional Cookies',
    icon: <Settings className="w-5 h-5" />,
    description: 'Enable enhanced functionality and personalization.',
    alwaysActive: false,
    examples: ['Language preferences', 'Region settings', 'Font size choices']
  },
  {
    type: 'marketing' as const,
    title: 'Marketing Cookies',
    icon: <Target className="w-5 h-5" />,
    description: 'Used to track visitors across websites for advertising purposes.',
    alwaysActive: false,
    examples: ['Ad targeting', 'Campaign measurement', 'Social media integration']
  }
] as const;

const COOKIE_DETAILS = [
  {
    name: 'session_id',
    purpose: 'Maintain user session state',
    duration: 'Session',
    type: 'Necessary'
  },
  {
    name: 'csrf_token',
    purpose: 'Cross-site request forgery protection',
    duration: 'Session',
    type: 'Necessary'
  },
  {
    name: 'user_preferences',
    purpose: 'Store user interface preferences',
    duration: '1 year',
    type: 'Functional'
  },
  {
    name: 'analytics_id',
    purpose: 'Track user behavior and site usage',
    duration: '2 years',
    type: 'Analytics'
  },
  {
    name: 'marketing_consent',
    purpose: 'Record marketing cookie preferences',
    duration: '1 year',
    type: 'Marketing'
  }
] as const;

const POLICY_SECTIONS = [
  { id: 'what-are-cookies', label: 'What Are Cookies?' },
  { id: 'cookie-types', label: 'Types of Cookies' },
  { id: 'cookie-list', label: 'Cookie Details' },
  { id: 'manage-cookies', label: 'Manage Preferences' }
] as const;

const BROWSER_SETTINGS = [
  'Chrome: Settings → Privacy and Security → Cookies',
  'Firefox: Options → Privacy & Security → Cookies',
  'Safari: Preferences → Privacy → Cookies',
  'Edge: Settings → Privacy, search, and services → Cookies'
] as const;

const THIRD_PARTY_TOOLS = [
  'Use browser extensions for cookie management',
  'Enable "Do Not Track" in your browser',
  'Use private/incognito browsing modes',
  'Regularly clear your browser cookies'
] as const;

const CookiePolicy: React.FC = () => {
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>({
    necessary: true, // Always enabled
    analytics: true,
    functional: true,
    marketing: false
  });

  // Memoized SEO data
  const seoData = useMemo(() => ({
    title: 'Cookie Policy | TooliQ.ai',
    description: 'TooliQ.ai Cookie Policy - Learn about how we use cookies and similar technologies to enhance your experience on our AI tools directory.',
    canonical: 'https://tooliq.ai/cookie-policy',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Cookie Policy",
      "description": "TooliQ.ai Cookie Policy - Information about cookie usage and tracking technologies.",
      "url": "https://tooliq.ai/cookie-policy",
      "datePublished": LAST_UPDATED,
      "dateModified": LAST_UPDATED,
      "publisher": {
        "@type": "Organization",
        "name": "TooliQ.ai",
        "url": "https://tooliq.ai"
      }
    }
  }), []);

  // Event handlers
  const handleCookieToggle = useCallback((type: CookieType) => {
    if (type === 'necessary') return; // Cannot disable necessary cookies
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  }, []);

  const saveCookiePreferences = useCallback(() => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setShowCookieSettings(false);
  }, [cookiePreferences]);

  const toggleCookieSettings = useCallback(() => {
    setShowCookieSettings(prev => !prev);
  }, []);

  const closeCookieSettings = useCallback(() => {
    setShowCookieSettings(false);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={seoData.canonical} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoData.structuredData)}
        </script>
      </Helmet>

      {/* Header Section */}
      <HeaderSection />

      {/* Main Content */}
      <div className="relative max-w-6xl mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <Sidebar 
            onCookieSettingsClick={toggleCookieSettings}
          />

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            <WhatAreCookiesSection />
            <CookieTypesSection />
            <CookieDetailsSection />
            <ManageCookiesSection />
            <ContactInformationSection />
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showCookieSettings && (
        <CookieSettingsModal
          cookiePreferences={cookiePreferences}
          onToggle={handleCookieToggle}
          onSave={saveCookiePreferences}
          onClose={closeCookieSettings}
        />
      )}
    </div>
  );
};

// Extracted HeaderSection component
const HeaderSection: React.FC = () => (
  <div className="relative overflow-hidden">
    <div className="absolute inset-0 bg-grid-slate-200 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-800"></div>
    <div className="relative max-w-6xl mx-auto px-6 py-16 text-center">
      <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
        <Cookie className="w-4 h-4" />
        Cookie Policy
      </div>
      <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-800 via-purple-600 to-blue-600 dark:from-slate-100 dark:via-purple-400 dark:to-blue-400 bg-clip-text text-transparent mb-6">
        Cookie Policy
      </h1>
      <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
        We use cookies and similar technologies to enhance your experience, analyze traffic, 
        and personalize content. Learn about our cookie practices and manage your preferences.
      </p>
      <div className="mt-6 text-sm text-slate-500 dark:text-slate-400">
        Last updated: {LAST_UPDATED}
      </div>
    </div>
  </div>
);

// Extracted Sidebar component
interface SidebarProps {
  onCookieSettingsClick: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onCookieSettingsClick }) => (
  <div className="lg:col-span-1">
    <div className="sticky top-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
      <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
        <Eye className="w-4 h-4 text-purple-500" />
        Policy Sections
      </h3>
      <nav className="space-y-2">
        {POLICY_SECTIONS.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            className="block text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 py-2 px-3 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
          >
            {section.label}
          </a>
        ))}
      </nav>

      {/* Cookie Settings Button */}
      <button
        onClick={onCookieSettingsClick}
        className="w-full mt-8 inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors text-sm"
      >
        <Settings className="w-4 h-4" />
        Cookie Settings
      </button>

      {/* Quick Links */}
      <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
        <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3 text-sm">
          Related Policies
        </h4>
        <div className="space-y-2">
          <a
            href="/privacy-policy"
            className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
          >
            <Shield className="w-3 h-3" />
            Privacy Policy
          </a>
          <a
            href="/terms-of-service"
            className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
          >
            <Cookie className="w-3 h-3" />
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  </div>
);

// Extracted WhatAreCookiesSection component
const WhatAreCookiesSection: React.FC = () => (
  <div id="what-are-cookies" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 scroll-mt-20">
    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
      <Info className="w-6 h-6 text-blue-500" />
      What Are Cookies?
    </h2>
    <div className="prose dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 leading-relaxed">
      <p>
        Cookies are small text files that are stored on your device when you visit a website. 
        They are widely used to make websites work more efficiently and provide information to the website owners.
      </p>
      <p className="mt-4">
        We use cookies and similar tracking technologies to track activity on our service and 
        hold certain information. Cookies can be "persistent" or "session" cookies.
      </p>
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
          <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Session Cookies</h4>
          <p className="text-blue-700 dark:text-blue-400 text-sm">
            Temporary cookies that expire when you close your browser. Used for basic site functionality.
          </p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
          <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">Persistent Cookies</h4>
          <p className="text-purple-700 dark:text-purple-400 text-sm">
            Remain on your device for a set period. Remember your preferences across visits.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Extracted CookieTypesSection component
const CookieTypesSection: React.FC = () => (
  <div id="cookie-types" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 scroll-mt-20">
    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
      <Cookie className="w-6 h-6 text-purple-500" />
      Types of Cookies We Use
    </h2>
    <div className="grid gap-6">
      {COOKIE_TYPES.map((cookieType) => (
        <CookieTypeCard key={cookieType.type} cookieType={cookieType} />
      ))}
    </div>
  </div>
);

// Extracted CookieTypeCard component
interface CookieTypeCardProps {
  cookieType: typeof COOKIE_TYPES[number];
}

const CookieTypeCard: React.FC<CookieTypeCardProps> = ({ cookieType }) => (
  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
          {cookieType.icon}
        </div>
        <div>
          <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-lg">
            {cookieType.title}
          </h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
            {cookieType.description}
          </p>
        </div>
      </div>
      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
        cookieType.alwaysActive 
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
      }`}>
        {cookieType.alwaysActive ? 'Always Active' : 'Optional'}
      </div>
    </div>
    <div className="mt-4">
      <h4 className="font-medium text-slate-700 dark:text-slate-300 text-sm mb-2">Examples:</h4>
      <div className="flex flex-wrap gap-2">
        {cookieType.examples.map((example, exampleIndex) => (
          <span
            key={exampleIndex}
            className="bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs px-3 py-1 rounded-full border border-slate-200 dark:border-slate-600"
          >
            {example}
          </span>
        ))}
      </div>
    </div>
  </div>
);

// Extracted CookieDetailsSection component
const CookieDetailsSection: React.FC = () => (
  <div id="cookie-list" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 scroll-mt-20">
    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
      <BarChart3 className="w-6 h-6 text-green-500" />
      Detailed Cookie Information
    </h2>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700">
            <th className="text-left py-3 px-4 font-semibold text-slate-800 dark:text-slate-200">Cookie Name</th>
            <th className="text-left py-3 px-4 font-semibold text-slate-800 dark:text-slate-200">Purpose</th>
            <th className="text-left py-3 px-4 font-semibold text-slate-800 dark:text-slate-200">Duration</th>
            <th className="text-left py-3 px-4 font-semibold text-slate-800 dark:text-slate-200">Type</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {COOKIE_DETAILS.map((cookie, index) => (
            <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <td className="py-3 px-4 text-slate-700 dark:text-slate-300 font-mono text-xs">{cookie.name}</td>
              <td className="py-3 px-4 text-slate-600 dark:text-slate-400">{cookie.purpose}</td>
              <td className="py-3 px-4 text-slate-600 dark:text-slate-400">{cookie.duration}</td>
              <td className="py-3 px-4">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  getCookieTypeClass(cookie.type)
                }`}>
                  {cookie.type}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Helper function for cookie type styling
const getCookieTypeClass = (type: string): string => {
  switch (type) {
    case 'Necessary':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'Functional':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'Analytics':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
    case 'Marketing':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    default:
      return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
  }
};

// Extracted ManageCookiesSection component
const ManageCookiesSection: React.FC = () => (
  <div id="manage-cookies" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 scroll-mt-20">
    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
      <Settings className="w-6 h-6 text-purple-500" />
      Manage Your Cookie Preferences
    </h2>
    <div className="prose dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 leading-relaxed">
      <p>
        You can control and/or delete cookies as you wish. You can delete all cookies that are 
        already on your computer and you can set most browsers to prevent them from being placed.
      </p>
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <ListSection 
          title="Browser Settings" 
          items={BROWSER_SETTINGS} 
        />
        <ListSection 
          title="Third-Party Tools" 
          items={THIRD_PARTY_TOOLS} 
        />
      </div>
    </div>
  </div>
);

// Extracted ListSection component
interface ListSectionProps {
  title: string;
  items: readonly string[];
}

const ListSection: React.FC<ListSectionProps> = ({ title, items }) => (
  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3">{title}</h4>
    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
      {items.map((item, index) => (
        <li key={index} className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-500" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

// Extracted ContactInformationSection component
const ContactInformationSection: React.FC = () => (
  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800 p-6 text-center">
    <h3 className="font-bold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2 justify-center">
      <Mail className="w-4 h-4" />
      Questions About Cookies?
    </h3>
    <p className="text-blue-700 dark:text-blue-400 text-sm mb-4">
      If you have any questions about our use of cookies or this Cookie Policy, 
      please contact our privacy team.
    </p>
    <a
      href="mailto:privacy@tooliq.ai"
      className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-xl transition-colors text-sm"
    >
      <Mail className="w-4 h-4" />
      privacy@tooliq.ai
    </a>
  </div>
);

// Extracted CookieSettingsModal component
interface CookieSettingsModalProps {
  cookiePreferences: CookiePreferences;
  onToggle: (type: CookieType) => void;
  onSave: () => void;
  onClose: () => void;
}

const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({
  cookiePreferences,
  onToggle,
  onSave,
  onClose
}) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
          <Settings className="w-5 h-5 text-purple-500" />
          Cookie Preferences
        </h3>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4 mb-6">
        {COOKIE_TYPES.map((cookieType) => (
          <div key={cookieType.type} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {cookieType.icon}
                <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                  {cookieType.title}
                </span>
              </div>
              <p className="text-slate-600 dark:text-slate-400 text-xs">
                {cookieType.description}
              </p>
            </div>
            <button
              onClick={() => onToggle(cookieType.type)}
              disabled={cookieType.alwaysActive}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                cookiePreferences[cookieType.type] || cookieType.alwaysActive
                  ? 'bg-purple-600'
                  : 'bg-slate-300 dark:bg-slate-600'
              } ${cookieType.alwaysActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-label={`Toggle ${cookieType.title}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  cookiePreferences[cookieType.type] || cookieType.alwaysActive
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-semibold py-3 px-4 rounded-xl transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
        >
          Save Preferences
        </button>
      </div>
    </div>
  </div>
);

export default React.memo(CookiePolicy);